import type { Model } from '@/helpers/models/Model'
import { Filter, type FilterOp } from './Filter'
import type { FilterControls } from './FilterControls'
import type { Filter as ApiFilter } from '@/interfaces/models/Filter'
import QueryBuilder from '../models/QueryBuilder'

export type MatchMode = 'all' | 'any'

export type QueryFilter = { key: string; op: FilterOp }
export type URLEncodedQueryFilters = { f?: string; m?: 'any' | 'all'; s?: string }

export class Filters<FilterModel extends Model> {
	list: Set<Filter<FilterModel>>
	mode: MatchMode
	search: string = ''
	controls: FilterControls<FilterModel>

	constructor(
		controls: FilterControls<FilterModel>,
		params?: {
			list?: Iterable<Filter<FilterModel>> | Set<Filter<FilterModel>>
			mode?: MatchMode
			search?: string
		},
	) {
		this.list = new Set(params?.list ?? [])
		this.mode = params?.mode ?? 'all'
		this.search = params?.search ?? ''
		this.controls = controls
	}

	clear(): void {
		this.list = new Set()
	}

	static fromQuery<FilterModel extends Model>(
		query: URLEncodedQueryFilters | null = null,
		controls: FilterControls<FilterModel>,
	): Filters<FilterModel> {
		const list: Filter<FilterModel>[] = []
		if (!query) return new Filters(controls, { list: new Set(list), mode: 'all', search: '' })

		if (query.f) {
			const filters: QueryFilter[] = JSON.parse(decodeURIComponent(query.f))
			for (const filter of filters) {
				const control = controls.items.find((control) => control.key === filter.key)
				if (!control) continue

				list.push(
					new Filter({
						key: filter.key,
						operator: filter.op,
						updateQuery: control.queryUpdateHandler,
					}),
				)
			}
		}

		const mode: MatchMode = query.m === 'any' ? 'any' : 'all'
		const search: string | undefined = query.s ? String(query.s) : undefined

		return new Filters(controls, { list: new Set(list), mode, search })
	}

	toQueryObject(): URLEncodedQueryFilters {
		const filters = Array.from(this.list).map(({ key, operator: op }) => ({
			key,
			op,
		}))

		const query: URLEncodedQueryFilters = {
			m: this.mode,
			s: this.search,
			f: encodeURIComponent(JSON.stringify(filters)),
		}

		if (!query.m || !filters || filters.length === 0) delete query.m
		if (!query.s) delete query.s
		if (!filters || filters.length === 0) delete query.f

		return query
	}

	getFilter(): ApiFilter {
		const qb = new QueryBuilder<FilterModel>()
		if (this.mode === 'all') {
			for (const filter of this.list) {
				if (!filter.updateQuery) continue
				qb.where(filter.updateQuery)
			}
		} else {
			for (const filter of this.list) {
				if (!filter.updateQuery) continue
				qb.orWhere(filter.updateQuery)
			}
		}
		return qb.getFilter()
	}

	toggleMode() {
		if (this.mode === 'all') this.mode = 'any'
		else this.mode = 'all'
	}
}
